import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import ContactVerificationPrompt from "../../common/components/ContactVerificationPrompt";
import Confirmation from "../../common/components/Confirmation.js";
import AlertMessage from "../../common/components/AlertMessage.js";
import Backdrop from "@mui/material/Backdrop";
import { CONTACT_VERIFICATION_ROLE, markContactVerificationPromptShown } from "../../common/utils/contactVerification";
import { resolveHomeVerificationPrompt } from "../../common/utils/homeVerificationPrompt";
import { getSessionContext } from "../../common/utils/sessionContext";
import { removerFromLocalStorage, setManyToLocalStorage, setToLocalStorage } from "../../common/utils/localStorage";
import * as APIService from "../../common/api/index.js";
import URL from "../../common/api/ConstantURL.js";
import { BiTask } from "react-icons/bi";
import { FaCheckCircle } from "react-icons/fa";
import { IoChevronBackCircleSharp } from "react-icons/io5";
import { BsChatLeftText, BsQrCodeScan } from "react-icons/bs";
import * as Sentry from "@sentry/react";

const fetchManualCheckinStatus = async () => {
  try {
    const responseData = await APIService.getData(URL.manualCheckinOptionStatus);
    return responseData.code == 200 && responseData.data === true;
  } catch (error) {
    Sentry.captureException(error);
    return false;
  }
};

const Home = () => {
  const { residentId, caregiverCheckinId, communityFlag, communityName } = getSessionContext();
  const hasResidentId = !!residentId;
  const hasCaregiverCheckinId = !!caregiverCheckinId;
  const hideCheckinMenu = hasResidentId && hasCaregiverCheckinId;
  const navigate = useNavigate();
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [verificationPromptMessage, setVerificationPromptMessage] = useState("");
  const [showTodoPopup, setShowTodoPopup] = useState(false);
  const [showResidentValue, setShowResidentValue] = useState(null);
  const [showResidentList, setShowResidentList] = useState([]);
  const [showResidentId, setShowResidentId] = useState(0);
  const [alertStatus, setAlertStatusBase] = useState("");
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [manualCheckinEnabled, setManualCheckinEnabled] = useState(null);

  useEffect(() => {
    if (hideCheckinMenu) return;
    let cancelled = false;
    fetchManualCheckinStatus().then((enabled) => {
      if (!cancelled) setManualCheckinEnabled(enabled);
    });
    return () => {
      cancelled = true;
    };
  }, [hideCheckinMenu]);

  useEffect(() => {
    let cancelled = false;

    const loadVerificationPrompt = async () => {
      const result = await resolveHomeVerificationPrompt(CONTACT_VERIFICATION_ROLE.CAREGIVER);
      if (cancelled || !result.shouldShow) {
        return;
      }

      setVerificationPromptMessage(result.message);
      setShowVerificationPrompt(true);
      markContactVerificationPromptShown(CONTACT_VERIFICATION_ROLE.CAREGIVER);
    };

    loadVerificationPrompt();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCloseVerificationPrompt = () => {
    setShowVerificationPrompt(false);
    markContactVerificationPromptShown(CONTACT_VERIFICATION_ROLE.CAREGIVER);
  };

  const handleChange = (e, pathname) => {
    e.preventDefault();
    navigate("/" + pathname);
  };

  const closeTodoConfirmation = () => setShowTodoPopup(false);

  const handleQRScanner = async (residentListOverride) => {
    try {
      const response = await window.flutter_inappwebview.callHandler("scanQR");
      const { status, data } = response;
      if (!status) return;
      const residentList = residentListOverride || showResidentList;
      const resident = residentList.find((item) => item.unit == data);
      if (!resident) {
        setAlertStatusBase({
          message: "This client isn't assigned to you. Please Contact Administrator.",
          status: "403",
          origin: "center",
        });
        return;
      }
      setShowResidentValue(resident.label);
      setShowResidentId(resident.id);
      setShowTodoPopup(true);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleCheckinClick = async () => {
    let enabled = manualCheckinEnabled;
    if (enabled === null) {
      setCheckinLoading(true);
      enabled = await fetchManualCheckinStatus();
      setCheckinLoading(false);
      setManualCheckinEnabled(enabled);
    }
    if (enabled) {
      navigate("/new-checkin");
      return;
    }
    await openCameraCheckin();
  };

  const openCameraCheckin = async () => {
    if (typeof window.flutter_inappwebview === "undefined") {
      setAlertStatusBase({
        message: "QR scanning is only supported inside the app. Please open this in the mobile application.",
        status: "400",
        origin: "center",
      });
      return;
    }

    setCheckinLoading(true);
    try {
      const { caregiverId, communityId } = getSessionContext();
      const responseData = await APIService.getData(URL.getAssociatedClientsByCaregiverId + "?caregiverId=" + caregiverId + "&communityId=" + communityId);
      if (responseData.code != 200) {
        setCheckinLoading(false);
        setAlertStatusBase({
          message: responseData.message,
          status: responseData.code,
          origin: "center",
        });
        return;
      }

      const updatedResidentList = [];
      const residentIdList = [];
      (responseData.data || []).forEach((resident) => {
        updatedResidentList.push({
          label: resident.clientName,
          id: resident.clientId,
          unit: resident.unit,
        });
        residentIdList.push(resident.clientId);
      });
      setToLocalStorage("ResidentIdList", JSON.stringify(residentIdList));
      setShowResidentList(updatedResidentList);
      setCheckinLoading(false);
      await handleQRScanner(updatedResidentList);
    } catch (error) {
      Sentry.captureException(error);
      setCheckinLoading(false);
      setAlertStatusBase({
        message: "Error while fetch associated clients for caregiver",
        status: "400",
        origin: "center",
      });
    }
  };

  const confirmationCallback = async (e) => {
    e.preventDefault();
    setCheckinLoading(true);
    const { caregiverId } = getSessionContext();
    const data = {
      caregiverId,
      clientId: showResidentId,
    };
    try {
      const responseData = await APIService.postData(URL.caregiverCheckin, data);
      setCheckinLoading(false);
      if (responseData.code == 200) {
        const selectedResident = showResidentList.find((resident) => resident.id == showResidentId);
        setShowTodoPopup(false);
        setManyToLocalStorage({
          ResidentName: showResidentValue,
          ResidentId: showResidentId,
          roomNumber: selectedResident?.unit || "",
          CaregiverCheckinId: responseData.data.caregiverCheckinDetailsId,
          CheckinTime: responseData?.data?.checkinTime || new Date().toISOString(),
        });
        navigate("/newResident");
      } else {
        setAlertStatusBase({
          message: responseData.message,
          status: responseData.code,
          origin: "center",
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      setCheckinLoading(false);
      setAlertStatusBase({
        message: "Error while checkin resident",
        status: "400",
        origin: "center",
      });
    }
  };

  const backToCommunitySelection = () => {
    removerFromLocalStorage("communityId");
    removerFromLocalStorage("communityName");
    navigate("/select-community");
  };

  const showBackIcon = communityFlag == 1;

  return (
    <>
      <Header />
      <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={checkinLoading}>
        <img src={require("../../assets/btTree.png")} className="loading-img" alt="Loading" />
      </Backdrop>
      {alertStatus ? <AlertMessage status={alertStatus.status} message={alertStatus.message} origin={alertStatus.origin} closeAlert={() => setAlertStatusBase("")} /> : null}
      <div className="home-container">
        <div className="change-community-btn">
          <div className="back-icon-container">{showBackIcon && <IoChevronBackCircleSharp className="vitals-preview-header" onClick={backToCommunitySelection} style={{ fontSize: "30px" }} />}</div>
          <span className="welcome-title">
            <span className="welcome-title-main">Welcome to {communityName}!</span>
            <br />
            <span className="welcome-title-sub">Choose what you'd like to do next.</span>
          </span>
          <div className="back-icon-container" />
        </div>
        <div className="home-inner-container spaceAround" style={{ height: "80%" }}>
          {!hideCheckinMenu && (
            <div className="home-menus col-8 col-sm-8 col-md-8 col-lg-4 col-xl-4" onClick={handleCheckinClick}>
              {manualCheckinEnabled === false ? <BsQrCodeScan className="caregiver-menu-icons" /> : <FaCheckCircle className="caregiver-menu-icons" />}
              <span>{manualCheckinEnabled === false ? "QR Checkin" : "Checkin"}</span>
            </div>
          )}
          <div className="home-menus col-8 col-sm-8 col-md-8 col-lg-4 col-xl-4" onClick={(e) => handleChange(e, "all-notes")}>
            <BsChatLeftText className="caregiver-menu-icons" />
            <span>Notes</span>
          </div>

          <div className="home-menus col-8 col-sm-8 col-md-8 col-lg-4 col-xl-4" onClick={(e) => handleChange(e, "caregiver-task")}>
            <BiTask className="caregiver-menu-icons" />
            <span>Task</span>
          </div>
        </div>
      </div>
      <ContactVerificationPrompt open={showVerificationPrompt} onClose={handleCloseVerificationPrompt} role={CONTACT_VERIFICATION_ROLE.CAREGIVER} message={verificationPromptMessage} />
      <Confirmation username={showResidentValue} content="Are you sure you want to check-in?" open={showTodoPopup} closeConfirmation={closeTodoConfirmation} processCallback={confirmationCallback} />
    </>
  );
};

export default Home;
